import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';

const CHART_COLORS = ['#EA580C', '#F97316', '#FDBA74', '#0F172A', '#16A34A'];

const today = () => new Date().toISOString().slice(0, 10);

export default function Attendance() {
  const qc = useQueryClient();
  const { t } = useI18n();
  const [date, setDate] = useState(today());
  const { data: classes } = useQuery({ queryKey: ['classes'], queryFn: () => api.get('/classes').then((r) => r.data) });
  const [classId, setClassId] = useState(null);
  const cid = classId || classes?.[0]?.id;
  const { data: students } = useQuery({ queryKey: ['cls-students', cid], queryFn: () => api.get(`/classes/${cid}/students`).then((r) => r.data), enabled: !!cid });
  const { data: existing } = useQuery({ queryKey: ['att', cid, date], queryFn: () => api.get('/attendance', { params: { class_id: cid, date } }).then((r) => r.data), enabled: !!cid });
  const { data: conflicts } = useQuery({ queryKey: ['conflicts'], queryFn: () => api.get('/attendance/conflicts').then((r) => r.data) });
  const { data: trends } = useQuery({ queryKey: ['att-trends'], queryFn: () => api.get('/attendance/trends', { params: { days: 14 } }).then((r) => r.data) });
  const [marks, setMarks] = useState({});

  useEffect(() => {
    const m = {};
    (existing || []).forEach((a) => { m[a.student_id] = a.status; });
    setMarks(m);
  }, [existing]);

  const submitMut = useMutation({
    mutationFn: () => api.post('/attendance', { class_id: cid, date, source: 'online', entries: (students || []).map((s) => ({ student_id: s.id, status: marks[s.id] || 'present' })) }),
    onSuccess: (r) => {
      toast.success(`Marked ${r.data.marked}${r.data.conflicts ? ` · ${r.data.conflicts} conflict(s) flagged for human resolution` : ''}`);
      qc.invalidateQueries({ queryKey: ['att'] }); qc.invalidateQueries({ queryKey: ['conflicts'] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  const resolveMut = useMutation({
    mutationFn: ({ id, status }) => api.post(`/attendance/conflicts/${id}/resolve`, { resolved_status: status }),
    onSuccess: () => { toast.success('Conflict resolved'); qc.invalidateQueries({ queryKey: ['conflicts'] }); qc.invalidateQueries({ queryKey: ['att'] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const setAll = (status) => { const m = {}; (students || []).forEach((s) => { m[s.id] = status; }); setMarks(m); };

  return (
    <div data-testid="attendance-page">
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mb-6">{t('Attendance')}</h1>

      {trends?.series?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-4 sm:p-5 mb-6 stagger-in" data-testid="attendance-trends-card">
          <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-3">{t('Attendance trend - last 14 days (%)')}</p>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trends.series} margin={{ top: 4, right: 8, bottom: 0, left: -18 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#64748B' }} tickFormatter={(d) => d.slice(5)} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: '#64748B' }} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, border: '1px solid #E2E8F0' }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {trends.classes.map((c, i) => (
                  <Line key={c} type="monotone" dataKey={c} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={{ r: 2 }} connectNulls />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {(conflicts || []).length > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-md p-4 mb-6" data-testid="conflicts-panel">
          <p className="font-display font-semibold text-amber-900 mb-2">⚠ {conflicts.length} offline-sync conflict(s) need human resolution - never silently overwritten</p>
          {conflicts.map((c) => (
            <div key={c.id} className="flex flex-wrap items-center gap-3 text-sm py-2 border-t border-amber-200">
              <span className="font-medium">{c.student_name}</span>
              <span className="font-mono-label text-[10px]">{c.date}</span>
              <span>existing: <b>{c.existing_status}</b> vs incoming ({c.incoming_source}): <b>{c.incoming_status}</b></span>
              <div className="flex gap-2 ml-auto">
                <button data-testid={`conflict-keep-${c.id}`} onClick={() => resolveMut.mutate({ id: c.id, status: c.existing_status })} className="text-xs bg-white border border-amber-300 rounded px-2 py-1 hover:border-amber-600">Keep {c.existing_status}</button>
                <button data-testid={`conflict-accept-${c.id}`} onClick={() => resolveMut.mutate({ id: c.id, status: c.incoming_status })} className="text-xs bg-amber-600 text-white rounded px-2 py-1 hover:bg-amber-500">Accept {c.incoming_status}</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4 items-center">
        {(classes || []).map((c) => (
          <button key={c.id} data-testid={`att-class-${c.grade}${c.section}`} onClick={() => setClassId(c.id)}
            className={`border rounded-md px-4 py-2 text-sm font-medium transition-colors ${cid === c.id ? 'bg-slate-900 text-white border-slate-900' : 'bg-white border-slate-200 hover:border-slate-900'}`}>
            Grade {c.grade}{c.section}
          </button>
        ))}
        <input type="date" data-testid="att-date-input" value={date} onChange={(e) => setDate(e.target.value)} className="border border-slate-200 rounded-md px-3 py-2 text-sm bg-white ml-auto" />
        <button data-testid="att-all-present" onClick={() => setAll('present')} className="text-xs border border-slate-200 bg-white rounded-md px-3 py-2 hover:border-green-700">{t('All present')}</button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto" data-testid="attendance-grid">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 sticky top-0"><tr className="text-left">
            <th className="py-2 px-3 font-mono-label text-[10px] uppercase text-slate-400">Student</th>
            <th className="py-2 px-3 font-mono-label text-[10px] uppercase text-slate-400">Status</th>
          </tr></thead>
          <tbody>
            {(students || []).map((s, i) => (
              <tr key={s.id} className={`border-t border-slate-100 ${i % 2 ? 'bg-slate-50/50' : ''}`}>
                <td className="py-2 px-3 font-medium">{s.name} <span className="font-mono-label text-[10px] text-slate-400">{s.admission_no}</span></td>
                <td className="py-2 px-3">
                  <div className="flex gap-1">
                    {['present', 'absent', 'late'].map((st) => (
                      <button key={st} data-testid={`att-${st}-${s.id}`} onClick={() => setMarks({ ...marks, [s.id]: st })}
                        className={`text-xs rounded px-2 py-1 capitalize transition-colors ${(marks[s.id] || 'present') === st
                          ? st === 'present' ? 'bg-green-700 text-white' : st === 'absent' ? 'bg-rose-600 text-white' : 'bg-amber-500 text-white'
                          : 'border border-slate-200 hover:border-slate-400'}`}>{t(st)}</button>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Button data-testid="att-submit-button" onClick={() => submitMut.mutate()} disabled={submitMut.isPending || !students?.length} className="mt-4 bg-slate-900 hover:bg-slate-800">{t('Save attendance')}</Button>
    </div>
  );
}
